import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { PreWorkInterviewStepType } from './PreWorkInterviewStepType';
import { useBatchId } from '../../../hooks/useBatchId';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useTranslation } from 'react-i18next';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { useCallApi } from '../../../hooks/useCallApi';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import {
	Answer,
	PreworkResponseData,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	transformAnswerToGRPC,
	transformGRPCToPreworkMentorAnswers,
} from '../../../shared/Utils';
import {
	EvaluationCategoryID,
	PreworkCompletionStatus,
	PreWorkQuestionId,
} from '../../../shared/Constants';
import { getPreworkDataByApplication } from '../../../service/VolunteerApplicationService';
import { PRE_WORK_APPLICATIONS } from '../../../shared/Routes';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import {
	capturePreworkMentorAnswers,
	submitApproverPreworkRecommendation,
} from '../../../service/PreWorkService';
import {
	PreworkCompletionStatus as PreworkStatus,
	PreworkMentorAnswersRequest,
	PreworkFeedback,
	PreworkRecommendationRequest,
	PreworkReviewAction,
} from '../../../shared/PreWorkServiceTypes';
import { useAuth } from '../../../hooks/useAuth';
import useUserSession from '../../../hooks/useUserSession';

interface Props {
	children: ReactNode;
}

export interface PreWorkInterviewContextType {
	preWorkMentorCategoryAnswers: PreWorkInterviewStepType;
	setPreWorkMentorCategoryAnswers: (answers: PreWorkInterviewStepType) => void;
	onSubmitClick: () => void;
	onCancelClick: () => void;
	preWorkData: Partial<PreworkResponseData>;
	preworkFeedback: PreworkFeedback;
	setPreworkFeedback: (feedback: PreworkFeedback) => void;
}

const PreWorkInterviewContext = createContext<PreWorkInterviewContextType>({
	preWorkMentorCategoryAnswers: { iConfirmAllTheAboveInfoIsTrue: false },
	setPreWorkMentorCategoryAnswers: () => {},
	onSubmitClick: () => {},
	onCancelClick: () => {},
	preWorkData: {},
	preworkFeedback: {},
	setPreworkFeedback: () => {},
});

export const PreWorkInterviewProvider: FC<Props> = ({ children }: Props) => {
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const { i18n } = useTranslation();
	const { batchId, navigateToRoute } = useBatchId();
	const { applicationId } = useApplicationId();
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const isApprover = selectedRole === UserRole.APPROVER;
	const [preWorkMentorCategoryAnswers, setPreWorkMentorCategoryAnswers] =
		useState<PreWorkInterviewStepType>({
			iConfirmAllTheAboveInfoIsTrue: false,
		});
	const [preWorkData, setPreWorkData] = useState<Partial<PreworkResponseData>>(
		{}
	);
	const [preworkFeedback, setPreworkFeedback] = useState<PreworkFeedback>({});
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();

	const onConfirmMentorSubmit = useCallback(async () => {
		const answers: Array<Answer> = [];
		const preWorkAnswers = cloneDeep(preWorkMentorCategoryAnswers);
		delete preWorkAnswers.iConfirmAllTheAboveInfoIsTrue;
		answers.push(
			...transformAnswerToGRPC(EvaluationCategoryID.PRE_WORK, preWorkAnswers)
		);
		let preworkStatus = '';
		const successMessage = 'thankYouForCompletingPreWork';

		if (
			preWorkAnswers[`${PreWorkQuestionId.MENTOR_SYSTEM_GENERATED}`]
				.selectedOption === 1
		) {
			preworkStatus = PreworkCompletionStatus.COMPLETED;
		} else if (
			preWorkAnswers[`${PreWorkQuestionId.MENTOR_SYSTEM_GENERATED}`]
				.selectedOption === 2
		) {
			preworkStatus = PreworkCompletionStatus.PARTIALLY_COMPLETED;
		} else if (
			preWorkAnswers[`${PreWorkQuestionId.MENTOR_SYSTEM_GENERATED}`]
				.selectedOption === 3
		) {
			preworkStatus = PreworkCompletionStatus.INCOMPLETE;
		}

		const request: PreworkMentorAnswersRequest = {
			batchId,
			applicationId,
			mentorPreworkAnswers: {
				questionsAndAnswers: answers,
				language: i18n.language,
			},
			mentorCompletionStatus: preworkStatus as PreworkStatus,
			isForwardedToApprover:
				preWorkAnswers[`${PreWorkQuestionId.MENTOR_CONCERNS_QUESTION}`]
					.selectedOption === 1,
		};

		try {
			await callApi(
				() => capturePreworkMentorAnswers(request),
				'errorOccurredWhileSubmittingApplication'
			);
			infoPopUpAction(
				'preworkSubmitted',
				successMessage,
				() => {
					navigateToRoute(PRE_WORK_APPLICATIONS);
				},
				ConfirmTick
			);
		} catch (e) {
			// handled in callApi
		}
	}, [
		preWorkMentorCategoryAnswers,
		batchId,
		applicationId,
		i18n.language,
		navigateToRoute,
		callApi,
		infoPopUpAction,
	]);

	const onConfirmApproverSubmit = useCallback(async () => {
		const feedback = cloneDeep(preworkFeedback);
		const successMessage = 'thankYouForSubmittingPreWorkFeedback';

		const request: PreworkRecommendationRequest = {
			batchId,
			applicationId,
			approverPreworkFeedback: {
				comments: feedback.comments,
				preworkRecommendation: feedback.preworkRecommendation,
				predefinedReasons: feedback.predefinedReasons,
			},
			functionarySrcmId: srcmId,
			selectedRoleCode: selectedRole as UserRole,
		};

		try {
			await callApi(
				() => submitApproverPreworkRecommendation(request),
				'errorOccurredWhileSubmittingApplication'
			);
			infoPopUpAction(
				'approverFeedbackSubmitted',
				successMessage,
				() => {
					navigateToRoute(PRE_WORK_APPLICATIONS);
				},
				ConfirmTick
			);
		} catch (e) {
			// handled in callApi
		}
	}, [
		applicationId,
		batchId,
		callApi,
		infoPopUpAction,
		navigateToRoute,
		preworkFeedback,
		selectedRole,
		srcmId,
	]);

	const onSubmitClick = useCallback(() => {
		if (
			preWorkData.mentorcompletionStatus === 'COMPLETED' ||
			(isApprover &&
				preWorkData.approverPreworkRecommendation &&
				preWorkData.approverPreworkRecommendation !==
					PreworkReviewAction.PENDING)
		) {
			navigateToRoute(PRE_WORK_APPLICATIONS);
			return;
		}
		if (isApprover) {
			confirmPopUpAction(
				'confirmSubmit',
				'areYouSureYouWantToSubmit',
				onConfirmApproverSubmit
			);
		} else {
			confirmPopUpAction(
				'confirmSubmit',
				'areYouSureYouWantToSubmit',
				onConfirmMentorSubmit
			);
		}
	}, [
		preWorkData.mentorcompletionStatus,
		preWorkData.approverPreworkRecommendation,
		isApprover,
		navigateToRoute,
		confirmPopUpAction,
		onConfirmApproverSubmit,
		onConfirmMentorSubmit,
	]);

	const value = useMemo<PreWorkInterviewContextType>(() => {
		const onCancelClick = () => {
			navigateToRoute(PRE_WORK_APPLICATIONS);
		};

		return {
			preWorkMentorCategoryAnswers,
			setPreWorkMentorCategoryAnswers,
			onSubmitClick,
			onCancelClick,
			preWorkData,
			preworkFeedback,
			setPreworkFeedback,
		};
	}, [
		preWorkMentorCategoryAnswers,
		onSubmitClick,
		preWorkData,
		navigateToRoute,
		preworkFeedback,
	]);

	const fetchPreworkDataByApplication = useCallback(async () => {
		if (!applicationId) {
			showToastMessage('error', 'applicationId is missing');
		}

		setLoading(true);

		try {
			const preworkData = await getPreworkDataByApplication({
				value: applicationId,
			});
			setPreWorkData(preworkData);
			const reasons = preworkData?.preworkFunctionaryPredefinedComments?.map(
				(pFPC) => pFPC.predefinedReason
			);
			setPreworkFeedback({
				comments: preworkData.approverComment,
				predefinedReasons: reasons,
				preworkRecommendation: preworkData.approverPreworkRecommendation,
			});
			if (
				preworkData.completionStatus !== PreworkCompletionStatus.COMPLETED ||
				preworkData.completionStatus !== PreworkCompletionStatus.NOT_STARTED
			) {
				setPreWorkMentorCategoryAnswers(
					transformGRPCToPreworkMentorAnswers(preworkData)
				);
			}
		} catch (e) {
			// Do Nothing
		} finally {
			setLoading(false);
		}
	}, [applicationId, setLoading, showToastMessage]);

	useEffect(() => {
		void fetchPreworkDataByApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<PreWorkInterviewContext.Provider value={value}>
			{children}
		</PreWorkInterviewContext.Provider>
	);
};

export const usePreWorkInterviewWizard = () => {
	return useContext(PreWorkInterviewContext);
};
